import { Link, useLocation } from 'react-router-dom';
import { useCallback, useEffect, type ReactNode } from 'react';
import { ConversationSidebar } from './ConversationSidebar';
import { UserProfileMenu } from './UserProfileMenu';
import { ResizeDivider } from './ResizeDivider';
import { SettingsModal } from './SettingsModal';
import { UpgradeModal } from './UpgradeModal';
import { useUIStore } from '../store/ui';
import { useChatStore } from '../store/chat';
import { useSettingsStore } from '../store/settings';
import { useCredits } from '../hooks/useCredits';
import { SidePanel } from './chat/SidePanel';
import type { Workflow } from '@n8n-builder/n8n-types';

function HamburgerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

interface LayoutProps {
  children: ReactNode;
}

// Icon components
function DashboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  );
}

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
    </svg>
  );
}

function MCPIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.39 48.39 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z" />
    </svg>
  );
}

function ImportIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
    </svg>
  );
}

function WorkflowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
    </svg>
  );
}

function TemplateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function LogoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 28 28" fill="none">
      <rect width="28" height="28" rx="7" fill="#0072FF" />
      <path d="M7.5 14h6m0 0l5.5-5m-5.5 5l5.5 5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CollapseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function ChevronIcon({ className, expanded }: { className?: string; expanded: boolean }) {
  return (
    <svg
      className={`${className} transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  );
}

const RECENT_CHATS_SECTION_ID = 'recent-chats';

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const isWorkflowBuilderPage = location.pathname.startsWith('/workflow-builder');
  const {
    sidePanelOpen,
    closeSidePanel,
    sidePanelWidth,
    setSidePanelWidth,
    resetSidePanelWidth,
    sidebarCollapsed,
    toggleSidebar,
    mobileSidebarOpen,
    openMobileSidebar,
    closeMobileSidebar,
    expandedSections,
    toggleSection,
    setSection,
  } = useUIStore();
  const { currentWorkflow, currentWorkflowId, currentPlan, currentPhase, startNewConversation, conversationId, getDebugLogs } = useChatStore();
  const debugLogs = conversationId ? getDebugLogs(conversationId) : [];

  // Debug logging
  if (debugLogs.length > 0) {
    console.log('[Debug] Layout has', debugLogs.length, 'debug logs for conversation', conversationId);
  }
  // Fetch credits on layout mount so the sidebar shows correct values immediately
  useCredits();

  const { hasValidCredentials, openSettingsModal } = useSettingsStore();

  // Initialize recent chats section as expanded by default
  useEffect(() => {
    if (expandedSections[RECENT_CHATS_SECTION_ID] === undefined) {
      setSection(RECENT_CHATS_SECTION_ID, true);
    }
  }, []);

  // Auto-close mobile sidebar on navigation
  useEffect(() => {
    closeMobileSidebar();
  }, [location.pathname]);

  const isRecentChatsExpanded = expandedSections[RECENT_CHATS_SECTION_ID] ?? true;

  // Resize handler (divider moves left = panel gets wider, since panel is on right)
  const handleResize = useCallback(
    (deltaX: number) => {
      setSidePanelWidth(sidePanelWidth - deltaX);
    },
    [sidePanelWidth, setSidePanelWidth]
  );

  // Deploy handler for the side panel
  const handleDeploy = async (workflow: Workflow, workflowId?: string) => {
    if (!hasValidCredentials()) {
      alert('No n8n instance configured. Go to Settings or MCP to configure one.');
      return;
    }
    // Dispatch custom event for ChatPage to handle
    window.dispatchEvent(new CustomEvent('deploy-workflow', { detail: { workflow, workflowId } }));
  };

  // Build handler for the side panel
  const handleBuild = () => {
    // Dispatch custom event for ChatPage to handle
    window.dispatchEvent(new CustomEvent('build-workflow'));
  };

  // Top-level navigation (Dashboard)
  const topNavItems = [
    { path: '/', label: 'Dashboard', icon: DashboardIcon },
  ];

  // Tools section navigation
  const toolsNavItems = [
    { path: '/workflow-builder', label: 'Workflow Builder', icon: ChatIcon },
    { path: '/connect', label: 'Connect to N8N', icon: MCPIcon },
    { path: '/import-workflows', label: 'Import Workflows', icon: ImportIcon },
  ];

  // Bottom navigation items
  const bottomNavItems = [
    { path: '/workflows', label: 'My Workflows', icon: WorkflowIcon },
    { path: '/templates', label: 'Template Library', icon: TemplateIcon },
  ];

  const renderNavItem = (item: { path: string; label: string; icon: React.FC<{ className?: string }> }, options?: { onClick?: () => void; isActive?: boolean; collapsed?: boolean }) => {
    const isCollapsed = options?.collapsed ?? sidebarCollapsed;
    // Default: active if current path matches item path
    // For workflow builder: only active if on that path AND no conversation loaded
    const isActive = options?.isActive !== undefined ? options.isActive : location.pathname === item.path;
    const Icon = item.icon;
    return (
      <li key={item.path}>
        <Link
          to={item.path}
          onClick={options?.onClick}
          title={isCollapsed ? item.label : undefined}
          className={`flex items-center gap-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
            isCollapsed ? 'justify-center px-2' : 'px-3'
          } ${
            isActive
              ? 'bg-primary-50 text-primary-700'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
          }`}
        >
          <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-primary-600' : 'text-gray-400'}`} />
          {!isCollapsed && item.label}
        </Link>
      </li>
    );
  };

  // Shared sidebar content renderer (used by desktop sidebar and mobile drawer)
  const renderSidebarContent = (collapsed: boolean) => (
    <>
      {/* Navigation */}
      <nav className={`flex-1 flex flex-col py-4 ${collapsed ? 'px-2' : 'px-3'} overflow-hidden`}>
        {/* Dashboard (top-level) */}
        <ul className="space-y-1">
          {topNavItems.map((item) => renderNavItem(item, { collapsed }))}
        </ul>

        {/* Tools Section */}
        {!collapsed && (
          <div className="mt-6">
            <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Tools
            </p>
          </div>
        )}
        <ul className={`space-y-1 ${collapsed ? 'mt-2' : ''}`}>
          {toolsNavItems.map((item) => {
            if (item.path === '/workflow-builder') {
              return renderNavItem(item, {
                onClick: startNewConversation,
                isActive: location.pathname.startsWith('/workflow-builder') && conversationId === null,
                collapsed,
              });
            }
            return renderNavItem(item, { collapsed });
          })}
        </ul>

        {/* Recent Chats Section */}
        <div className="flex-1 flex flex-col mt-4 min-h-0">
          {!collapsed && (
            <button
              onClick={() => toggleSection(RECENT_CHATS_SECTION_ID)}
              className="flex items-center justify-between px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600 transition-colors w-full"
            >
              <span>Recent Chats</span>
              <ChevronIcon className="h-3 w-3" expanded={isRecentChatsExpanded} />
            </button>
          )}

          {isRecentChatsExpanded && (
            <div className="flex-1 overflow-hidden">
              {collapsed ? (
                <div className="px-2 py-2">
                  <button
                    onClick={startNewConversation}
                    className="w-full p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 transition-all duration-200 flex justify-center"
                    title="New Chat"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
              ) : (
                <ConversationSidebar />
              )}
            </div>
          )}
        </div>

        {/* Bottom Nav Items */}
        <ul className="space-y-1 mt-4 pt-4 border-t border-gray-100">
          {bottomNavItems.map((item) => renderNavItem(item, { collapsed }))}
          <li>
            <button
              onClick={() => openSettingsModal()}
              title={collapsed ? 'Settings' : undefined}
              className={`flex w-full items-center gap-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                collapsed ? 'justify-center px-2' : 'px-3'
              } text-gray-600 hover:bg-gray-100 hover:text-gray-900`}
            >
              <SettingsIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
              {!collapsed && 'Settings'}
            </button>
          </li>
        </ul>
      </nav>

      {/* User Profile */}
      <div className={`mt-auto border-t border-gray-100 ${collapsed ? 'p-2' : 'p-4'}`}>
        <UserProfileMenu collapsed={collapsed} />
      </div>
    </>
  );

  return (
    <div className="flex flex-col md:flex-row h-screen bg-gray-50">
      {/* Mobile Top Bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white md:hidden">
        <button
          onClick={openMobileSidebar}
          className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <HamburgerIcon className="h-6 w-6" />
        </button>
        <Link to="/" className="flex items-center gap-2">
          <LogoIcon className="h-7 w-7" />
          <span className="text-lg font-semibold text-gray-900">FlowRouters</span>
        </Link>
        <div className="w-9" /> {/* Spacer to center logo */}
      </div>

      {/* Mobile Sidebar Drawer */}
      {mobileSidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50" onClick={closeMobileSidebar} />
          {/* Drawer */}
          <div className="fixed inset-y-0 left-0 z-50 w-64 flex flex-col bg-white shadow-xl animate-slide-in-left">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-3 py-4 border-b border-gray-100">
              <Link to="/" className="flex items-center gap-2.5 group">
                <LogoIcon className="h-7 w-7 flex-shrink-0 transition-transform duration-200 group-hover:scale-105" />
                <span className="text-xl font-semibold tracking-tight leading-none text-gray-900">
                  FlowRouters
                </span>
              </Link>
              <button
                onClick={closeMobileSidebar}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            {renderSidebarContent(false)}
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex flex-col bg-white border-r border-gray-200 shadow-sm transition-all duration-300 ease-in-out ${
        sidebarCollapsed ? 'w-16' : 'w-64'
      }`}>
        {/* Logo and Collapse Toggle */}
        <div className="flex items-center justify-between px-3 py-4 border-b border-gray-100">
          <Link to="/" className={`flex items-center gap-2.5 group ${sidebarCollapsed ? 'justify-center w-full' : ''}`}>
            <LogoIcon className="h-7 w-7 flex-shrink-0 transition-transform duration-200 group-hover:scale-105" />
            {!sidebarCollapsed && (
              <span className="text-xl font-semibold tracking-tight leading-none text-gray-900">
                FlowRouters
              </span>
            )}
          </Link>
          {!sidebarCollapsed && (
            <button
              onClick={toggleSidebar}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              title="Collapse sidebar"
            >
              <CollapseIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Expand button when collapsed */}
        {sidebarCollapsed && (
          <div className="px-3 py-2">
            <button
              onClick={toggleSidebar}
              className="w-full p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex justify-center"
              title="Expand sidebar"
            >
              <CollapseIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {renderSidebarContent(sidebarCollapsed)}
      </aside>

      {/* Content area: main + side panel */}
      <div className="flex flex-1 min-h-0">
        {/* Main Content */}
        <main className="flex-1 overflow-auto bg-gray-50">{children}</main>

        {/* Settings Modal */}
        <SettingsModal />
        <UpgradeModal />

        {/* Right Side Panel (Workflow Builder page only) */}
        {isWorkflowBuilderPage && sidePanelOpen && (
          <>
            {/* Mobile backdrop for side panel */}
            <div className="md:hidden fixed inset-0 z-30 bg-black/50" onClick={closeSidePanel} />

            {/* Resize divider (desktop only) */}
            <div className="hidden md:block h-full">
              <ResizeDivider
                onResize={handleResize}
                onDoubleClick={resetSidePanelWidth}
              />
            </div>

            <aside
              style={{ '--panel-width': `${sidePanelWidth}px`, transform: 'translateZ(0)' } as React.CSSProperties}
              className="fixed inset-0 z-40 md:relative md:inset-auto md:z-auto md:h-full md:flex-shrink-0 md:[width:var(--panel-width)] border-l border-gray-200 bg-white overflow-hidden animate-slide-in-right"
            >
              <SidePanel
                workflow={currentWorkflow}
                workflowId={currentWorkflowId}
                plan={currentPlan}
                debugLogs={debugLogs}
                onDeploy={handleDeploy}
                onBuild={handleBuild}
                isDeploying={false}
                isBuilding={currentPhase === 'building'}
              />
            </aside>
          </>
        )}
      </div>
    </div>
  );
}
